import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "react-query";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiMapPin,
  FiClock,
  FiPhone,
  FiMail,
  FiUsers,
  FiCalendar,
  FiArrowLeft,
  FiHeart,
  FiShare2,
  FiCheckCircle,
  FiTruck,
  FiDollarSign,
} from "react-icons/fi";
import { vendorsAPI } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";

const VendorDetailContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 0;

  @media (max-width: 768px) {
    padding: 1rem 0;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0;
  }
`;

const VendorDetailContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (max-width: 480px) {
    padding: 0 0.75rem;
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #2d5016;
  text-decoration: none;
  font-weight: 500;
  margin-bottom: 2rem;
  transition: color 0.3s ease;

  &:hover {
    color: #1a3a0e;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
    font-size: 0.875rem;
  }
`;

const VendorHeader = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1rem;
  }
`;

const VendorImage = styled.div`
  height: 300px;
  background: linear-gradient(135deg, #2d5016, #4a7c59);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 4rem;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px;
  }

  .placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  @media (max-width: 768px) {
    height: 250px;
    font-size: 3rem;
  }

  @media (max-width: 480px) {
    height: 200px;
    font-size: 2.5rem;
  }
`;

const VendorInfo = styled.div`
  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 0.5rem;

    @media (max-width: 768px) {
      font-size: 2rem;
    }

    @media (max-width: 480px) {
      font-size: 1.75rem;
    }
  }

  .cuisine-type {
    color: #666;
    font-size: 1.125rem;
    margin-bottom: 1rem;

    @media (max-width: 480px) {
      font-size: 1rem;
    }
  }

  .rating-section {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;

    @media (max-width: 480px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
    }

    .stars {
      display: flex;
      gap: 0.25rem;
      color: #ffc107;
    }

    .rating-text {
      color: #666;
      font-size: 1rem;

      @media (max-width: 480px) {
        font-size: 0.875rem;
      }
    }
  }

  .description {
    color: #666;
    line-height: 1.6;
    margin-bottom: 1.5rem;

    @media (max-width: 480px) {
      font-size: 0.875rem;
    }
  }

  .contact-info {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }

    .contact-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #666;
      font-size: 0.875rem;

      @media (max-width: 480px) {
        font-size: 0.8rem;
      }

      .icon {
        color: #2d5016;
        flex-shrink: 0;
      }
    }
  }

  .action-buttons {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;

    @media (max-width: 480px) {
      flex-direction: column;
      gap: 0.75rem;
    }
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid #2d5016;
  background: ${(props) => (props.primary ? "#2d5016" : "white")};
  color: ${(props) => (props.primary ? "white" : "#2d5016")};
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.primary ? "#1a3a0e" : "#2d5016")};
    color: white;
  }

  @media (max-width: 480px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.875rem;
    justify-content: center;
  }
`;

const TabsContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;

  @media (max-width: 480px) {
    margin-bottom: 1rem;
    border-radius: 8px;
  }
`;

const TabsHeader = styled.div`
  display: flex;
  border-bottom: 1px solid #e0e0e0;

  button {
    flex: 1;
    padding: 1rem 1.5rem;
    border: none;
    background: none;
    color: #666;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    border-bottom: 3px solid transparent;

    &.active {
      color: #2d5016;
      border-bottom-color: #2d5016;
    }

    &:hover:not(.active) {
      color: #333;
    }

    @media (max-width: 480px) {
      padding: 0.75rem 1rem;
      font-size: 0.875rem;
    }
  }
`;

const TabContent = styled.div`
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const MealPlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const MealPlanCard = styled(motion.div)`
  background: #f8f9fa;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: all 0.3s ease;

  &:hover {
    border-color: #2d5016;
    transform: translateY(-2px);
  }

  .meal-image {
    height: 150px;
    background: linear-gradient(135deg, #2d5016, #4a7c59);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 2rem;
    position: relative;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
    }
  }

  .meal-content {
    padding: 1.5rem;

    @media (max-width: 480px) {
      padding: 1rem;
    }

    h3 {
      font-size: 1.25rem;
      font-weight: 600;
      color: #2d5016;
      margin-bottom: 0.5rem;

      @media (max-width: 480px) {
        font-size: 1.125rem;
      }
    }

    .plan-type {
      display: inline-block;
      background: #2d5016;
      color: white;
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      margin-bottom: 1rem;
    }

    .description {
      color: #666;
      font-size: 0.875rem;
      line-height: 1.5;
      margin-bottom: 1rem;

      @media (max-width: 480px) {
        font-size: 0.8rem;
      }
    }

    .plan-details {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.5rem;
      margin-bottom: 1rem;

      @media (max-width: 480px) {
        grid-template-columns: 1fr;
        gap: 0.25rem;
      }

      .detail-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #666;
        font-size: 0.875rem;

        @media (max-width: 480px) {
          font-size: 0.8rem;
        }

        .icon {
          color: #2d5016;
          flex-shrink: 0;
        }
      }
    }

    .price-section {
      margin-bottom: 1rem;
      padding-top: 1rem;
      border-top: 1px solid #e0e0e0;

      .pricing-options {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .price-option {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .price-label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #2d5016;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .price {
        font-size: 1.25rem;
        font-weight: 700;
        color: #2d5016;

        @media (max-width: 480px) {
          font-size: 1.125rem;
        }
      }

      .price-breakdown {
        font-size: 0.7rem;
        color: #666;
        font-style: italic;
      }

      .price-note {
        font-size: 0.7rem;
        color: #666;
      }
    }

    .fulfillment-options {
      margin-bottom: 1rem;
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .fulfillment-badge {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.7rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;

      .icon {
        font-size: 0.8rem;
      }

      &.both {
        background: linear-gradient(135deg, #2d5016, #4a7c59);
        color: white;
      }

      &.delivery-only {
        background: #e8f5e8;
        color: #2d5016;
        border: 1px solid #2d5016;
      }

      &.pickup-only {
        background: #fff3e0;
        color: #e65100;
        border: 1px solid #e65100;
      }
    }
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.125rem;
  color: #666;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #dc3545;

  h3 {
    margin-bottom: 1rem;
  }
`;

const VendorDetail = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("meal-plans");
  const { isAuthenticated } = useAuth();

  const { data, isLoading, error } = useQuery(
    ["vendor", id],
    () => {
      console.log("VendorDetail - Making API call for vendor ID:", id);
      return vendorsAPI.getVendor(id);
    },
    {
      enabled: !!id,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const vendor = data?.data?.data?.vendor;
  const mealPlans = data?.data?.data?.mealPlans || [];
  const reviews = data?.data?.data?.recentReviews || [];

  const handleFollow = () => {
    if (!isAuthenticated) {
      toast.error("Please login to follow vendors");
      return;
    }
    toast.success("Follow feature coming soon! 🚀");
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${vendor.business_name} - NourishGY`,
        text: `Check out ${vendor.business_name} on NourishGY!`,
        url: window.location.href,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  if (isLoading) {
    return (
      <VendorDetailContainer>
        <VendorDetailContent>
          <LoadingState>
            <div className="loading-spinner" />
            Loading vendor details...
          </LoadingState>
        </VendorDetailContent>
      </VendorDetailContainer>
    );
  }

  if (error || !vendor) {
    return (
      <VendorDetailContainer>
        <VendorDetailContent>
          <ErrorState>
            <h3>Vendor not found</h3>
            <p>
              The vendor you're looking for doesn't exist or has been removed.
            </p>
            <BackButton to="/vendors">← Back to Vendors</BackButton>
          </ErrorState>
        </VendorDetailContent>
      </VendorDetailContainer>
    );
  }

  return (
    <VendorDetailContainer>
      <VendorDetailContent>
        <BackButton to="/vendors">
          <FiArrowLeft />
          Back to Vendors
        </BackButton>

        <VendorHeader>
          <VendorImage>
            {vendor.profile_image_url ? (
              <img src={vendor.profile_image_url} alt={vendor.business_name} />
            ) : (
              <div className="placeholder">
                <span>🍽️</span>
                <span>{vendor.business_name.charAt(0)}</span>
              </div>
            )}
          </VendorImage>

          <VendorInfo>
            <h1>{vendor.business_name}</h1>
            <div className="cuisine-type">{vendor.cuisine_type}</div>

            {vendor.description && (
              <div className="description">{vendor.description}</div>
            )}

            <div className="contact-info">
              <div className="contact-item">
                <FiMapPin className="icon" />
                <span>{vendor.business_address}</span>
              </div>
              {vendor.business_phone && (
                <div className="contact-item">
                  <FiPhone className="icon" />
                  <span>{vendor.business_phone}</span>
                </div>
              )}
              {vendor.business_email && (
                <div className="contact-item">
                  <FiMail className="icon" />
                  <span>{vendor.business_email}</span>
                </div>
              )}
              <div className="contact-item">
                <FiTruck className="icon" />
                <span>{vendor.delivery_radius}km delivery radius</span>
              </div>
            </div>

            <div className="action-buttons">
              <ActionButton primary onClick={handleFollow}>
                <FiHeart />
                Follow
              </ActionButton>
              <ActionButton onClick={handleShare}>
                <FiShare2 />
                Share
              </ActionButton>
            </div>
          </VendorInfo>
        </VendorHeader>

        <TabsContainer>
          <TabsHeader>
            <button
              className={activeTab === "meal-plans" ? "active" : ""}
              onClick={() => setActiveTab("meal-plans")}
            >
              Meal Plans ({mealPlans.length})
            </button>
          </TabsHeader>

          <TabContent>
            {activeTab === "meal-plans" && (
              <div>
                {mealPlans.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "2rem",
                      color: "#666",
                    }}
                  >
                    <h3>No meal plans available</h3>
                    <p>This vendor hasn't added any meal plans yet.</p>
                  </div>
                ) : (
                  <MealPlansGrid>
                    {mealPlans.map((plan, index) => (
                      <MealPlanCard
                        key={plan.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <div className="meal-image">
                          {plan.image_url ? (
                            <img src={plan.image_url} alt={plan.title} />
                          ) : (
                            <div className="placeholder">
                              <span>🍽️</span>
                              <span>{plan.title.charAt(0)}</span>
                            </div>
                          )}
                        </div>

                        <div className="meal-content">
                          <div className="plan-type">{plan.plan_type}</div>
                          <h3>{plan.title}</h3>
                          {plan.description && (
                            <div className="description">
                              {plan.description}
                            </div>
                          )}

                          <div className="plan-details">
                            <div className="detail-item">
                              <FiCalendar className="icon" />
                              <span>{plan.duration_days} days</span>
                            </div>
                          </div>

                          <div className="fulfillment-options">
                            {plan.offers_delivery && plan.offers_pickup && (
                              <div className="fulfillment-badge both">
                                <FiTruck className="icon" />
                                <span>Delivery & Pickup</span>
                              </div>
                            )}
                            {plan.offers_delivery && !plan.offers_pickup && (
                              <div className="fulfillment-badge delivery-only">
                                <FiTruck className="icon" />
                                <span>Delivery Only</span>
                              </div>
                            )}
                            {!plan.offers_delivery && plan.offers_pickup && (
                              <div className="fulfillment-badge pickup-only">
                                <FiMapPin className="icon" />
                                <span>Pickup Only</span>
                              </div>
                            )}
                          </div>

                          <div className="price-section">
                            <div className="pricing-options">
                              {plan.offers_delivery && (
                                <div className="price-option">
                                  <div className="price-label">Delivery</div>
                                  <div className="price">
                                    GYD $
                                    {Math.round(
                                      parseFloat(plan.base_price) +
                                        parseFloat(plan.delivery_fee || 0)
                                    ).toLocaleString()}
                                  </div>
                                  {plan.delivery_fee > 0 && (
                                    <div className="price-breakdown">
                                      Base: $
                                      {Math.round(
                                        parseFloat(plan.base_price)
                                      ).toLocaleString()}{" "}
                                      + Delivery: $
                                      {Math.round(
                                        parseFloat(plan.delivery_fee)
                                      ).toLocaleString()}
                                    </div>
                                  )}
                                </div>
                              )}
                              {plan.offers_pickup && (
                                <div className="price-option">
                                  <div className="price-label">Pickup</div>
                                  <div className="price">
                                    GYD $
                                    {Math.round(
                                      parseFloat(plan.base_price)
                                    ).toLocaleString()}
                                  </div>
                                  <div className="price-note">
                                    Pickup from{" "}
                                    {plan.pickup_address || "vendor location"}
                                  </div>
                                </div>
                              )}
                              {!plan.offers_delivery && !plan.offers_pickup && (
                                <div className="price-option">
                                  <div className="price">
                                    GYD $
                                    {Math.round(
                                      parseFloat(plan.price)
                                    ).toLocaleString()}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <Link
                            to={
                              isAuthenticated
                                ? `/checkout/${plan.id}`
                                : "/login"
                            }
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "100%",
                              padding: "0.75rem",
                              background: "#2d5016",
                              color: "white",
                              textDecoration: "none",
                              borderRadius: "6px",
                              fontWeight: "500",
                              transition: "background 0.3s ease",
                            }}
                          >
                            {isAuthenticated
                              ? "Purchase Now"
                              : "Login to Purchase"}
                          </Link>
                        </div>
                      </MealPlanCard>
                    ))}
                  </MealPlansGrid>
                )}
              </div>
            )}
          </TabContent>
        </TabsContainer>
      </VendorDetailContent>
    </VendorDetailContainer>
  );
};

export default VendorDetail;
